import { useCallback, useEffect, useRef, useState } from "react";
import { sprintNotificationsApi } from "./sprintNotificationsApi";

export const POLL_INTERVAL_MS = 60 * 1000;
// Rows per getNewSince call (the backend's limit) and how many pages one check follows.
const NEW_PAGE_SIZE = 20;
const MAX_NEW_PAGES = 5;

/**
 * Keeps the bell count for one sprint fresh and reports newly arrived
 * notifications, without websockets.
 *
 *   • Checks the unread count on open, every 60 s, on tab focus, and whenever
 *     `refreshKey` changes (the sprint page's Refresh button).
 *   • Skips the call while the tab is hidden or the user is logged out.
 *   • The first check of a sprint only records the latest id, so opening a
 *     sprint never replays old notifications as toasts.
 *   • When the latest id grows, fetches just the new rows and hands them to
 *     `onNew` (oldest first).
 */
export const useSprintNotificationPoll = (sprintId, { onNew, refreshKey } = {}) => {
    const [count, setCount] = useState(0);
    const lastSeenId = useRef(null);
    // Sprint id of the check in flight, so a slow answer for the previous sprint
    // neither blocks nor overwrites the newly opened one.
    const inFlight = useRef(null);
    // A check asked for while one was running (e.g. after "Mark all as read");
    // it runs as soon as the current one ends so the count is not left stale.
    const rerun = useRef(false);
    const activeSprint = useRef(sprintId);
    activeSprint.current = sprintId;
    const onNewRef = useRef(onNew);
    onNewRef.current = onNew;

    const check = useCallback(async () => {
        if (!sprintId) return;
        if (inFlight.current === sprintId) { rerun.current = true; return; }
        if (!localStorage.getItem("authToken")) return;
        if (document.visibilityState !== "visible") return;

        inFlight.current = sprintId;
        try {
            const res = await sprintNotificationsApi.getUnreadCount(sprintId);
            if (res.status !== "success" || activeSprint.current !== sprintId) return;
            const { count: unread, latest_id: latestId } = res.data;
            setCount(unread);

            if (lastSeenId.current === null) {
                lastSeenId.current = latestId;
            } else if (latestId > lastSeenId.current) {
                // Follow the pages so a burst of more than 20 is toasted in full.
                // lastSeenId only moves once rows are in hand, so a failed call
                // is retried by the next check instead of being skipped.
                const fresh = [];
                let after = lastSeenId.current;
                let full = false;
                for (let page = 0; page < MAX_NEW_PAGES; page++) {
                    const batch = await sprintNotificationsApi.getNewSince(sprintId, after);
                    if (activeSprint.current !== sprintId || batch.status !== "success") return;
                    fresh.push(...batch.data);
                    full = batch.data.length >= NEW_PAGE_SIZE;
                    if (!full) break;
                    after = batch.data[batch.data.length - 1].id;
                }
                lastSeenId.current = full ? after : latestId;
                if (fresh.length && onNewRef.current) onNewRef.current(fresh);
            }
        } catch (err) {
            // Background check: a missed poll is retried in 60 s, not worth a toast.
            // eslint-disable-next-line no-console
            console.warn("[sprint-notifications] poll failed:", err?.message || err);
        } finally {
            if (inFlight.current === sprintId) inFlight.current = null;
            if (rerun.current) {
                rerun.current = false;
                if (activeSprint.current === sprintId) checkRef.current();
            }
        }
    }, [sprintId]);
    const checkRef = useRef(check);
    checkRef.current = check;

    useEffect(() => {
        lastSeenId.current = null;
        rerun.current = false;
        setCount(0);
        if (!sprintId) return undefined;

        check();
        const interval = setInterval(check, POLL_INTERVAL_MS);
        const onVisibility = () => {
            if (document.visibilityState === "visible") check();
        };
        window.addEventListener("focus", check);
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", check);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [sprintId, check]);

    useEffect(() => {
        if (refreshKey !== undefined) check();
        // Only a change of refreshKey should trigger this extra check.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    return { count, refresh: check };
};
